import { type ComponentType } from 'react';

// Registry of page components that can be loaded from user config
// The key should match the `pageComponent` path in user config (e.g., "home/HomepageView")

type PageComponentProps = {
  config?: Record<string, unknown>;
};

type PageComponent = ComponentType<PageComponentProps>;

// Eager loaded components (always available)
import { HomepageView } from './Home/HomepageView';
import { TelemetryManagementPage } from './TelemetryManagement';
import { OrchestratorManagementPage } from './OrchestratorManagement';
import { NotificationManagementPage } from './NotificationManagement';
import AnomaliesManagementPage from './AnomaliesManagement';
import EquipmentCatalogPage from './EquipmentCatalog/EquipmentCatalogPage';
import { SimulationScenariosPage } from './SimulationScenarios/SimulationScenariosPage';

// Component registry - maps pageComponent paths to actual components
const componentRegistry: Record<string, PageComponent> = {
  // Home pages
  'home/HomepageView': HomepageView,

  // Management pages
  'telemetry/TelemetryManagementPage': TelemetryManagementPage,
  'orchestrator/OrchestratorManagementPage': OrchestratorManagementPage,
  'notification/NotificationManagementPage': NotificationManagementPage,
  'anomalies/AnomaliesManagementPage': AnomaliesManagementPage,
  'equipment/EquipmentCatalogPage': EquipmentCatalogPage,
  'simulation/SimulationScenariosPage': SimulationScenariosPage,

  // Add more components here as they are implemented
  // 'simpleViewer/SimpleViewerView': SimpleViewerView,
  // 'modelImport/SimpleModelImportView': SimpleModelImportView,
};

// ============================================================================
// BUILT-IN PAGES
// These pages are always available in the app, regardless of user config
// ============================================================================

export interface BuiltInPage {
  key: string;
  path: string;
  title: string;
  icon: string;
  Component: PageComponent;
  group?: string; // Optional group name for sidebar organization
}

export const builtInPages: BuiltInPage[] = [
    {
        key: 'equipment-catalog',
        path: '/admin/equipment-catalog',
        title: 'Equipment Catalog',
        icon: 'fas fa-tags',
        Component: EquipmentCatalogPage,
        group: 'Admin Tools',
    },{
    key: 'telemetry-management',
    path: '/admin/telemetry',
    title: 'Telemetry Management',
    icon: 'fas fa-chart-line',
    Component: TelemetryManagementPage,
    group: 'Admin Tools',
  },
  {
    key: 'simulation-scenarios',
    path: '/admin/simulation-scenarios',
    title: 'Simulation Scenarios',
    icon: 'fas fa-project-diagram',
    Component: SimulationScenariosPage,
    group: 'Admin Tools',
  },
  {
    key: 'orchestrator-management',
    path: '/admin/orchestrators',
    title: 'Orchestrator Management',
    icon: 'fas fa-clock',
    Component: OrchestratorManagementPage,
    group: 'Admin Tools',
  },
  {
    key: 'notification-management',
    path: '/admin/notifications',
    title: 'Notification Management',
    icon: 'fas fa-bell',
    Component: NotificationManagementPage,
    group: 'Admin Tools',
  },
  {
    key: 'anomalies-management',
    path: '/admin/anomalies',
    title: 'Anomalies Management',
    icon: 'fas fa-exclamation-triangle',
    Component: AnomaliesManagementPage,
    group: 'Admin Tools',
  },
  // Add more built-in pages here as needed
];

/**
 * Get a page component by its path from user config
 * @param pageComponentPath - The path from user config (e.g., "home/HomepageView")
 * @returns The component or null if not found
 */
export function getPageComponent(pageComponentPath: string): PageComponent | null {
  // Try exact match first
  if (componentRegistry[pageComponentPath]) {
    return componentRegistry[pageComponentPath];
  }

  // Try without leading path segments (e.g., "HomepageView" for "home/HomepageView")
  const fileName = pageComponentPath.split('/').pop();
  if (fileName) {
    const match = Object.entries(componentRegistry).find(([key]) =>
      key.endsWith('/' + fileName) || key === fileName
    );
    if (match) {
      return match[1];
    }
  }

  return null;
}

/**
 * Check if a page component is registered
 */
export function hasPageComponent(pageComponentPath: string): boolean {
  return getPageComponent(pageComponentPath) !== null;
}

/**
 * Get all registered component paths
 */
export function getRegisteredComponents(): string[] {
  return Object.keys(componentRegistry);
}

export default componentRegistry;


